import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Store, Plug, Warehouse, RefreshCw, Trash2, FileText, Zap, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Check, ChevronsUpDown } from 'lucide-react';
import { ChannelPricingRules, checkMarginGuard } from '@/components/ChannelPricingRules';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const GCC = [
  { code: 'ae', name: 'UAE' }, { code: 'sa', name: 'Saudi Arabia' }, { code: 'qa', name: 'Qatar' },
  { code: 'bh', name: 'Bahrain' }, { code: 'om', name: 'Oman' }, { code: 'kw', name: 'Kuwait' },
];

export default function DanubeHomeIntegrationPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [locations, setLocations] = useState([]);
  const [zohoCustomers, setZohoCustomers] = useState([]);
  const [zohoCustOpen, setZohoCustOpen] = useState(false);
  const [newMap, setNewMap] = useState({ app_location_id: '', countries: ['ae'], stock_buffer: 0 });
  const [syncing, setSyncing] = useState(false);
  const [syncingOrders, setSyncingOrders] = useState(false);
  const [ordersFromDate, setOrdersFromDate] = useState('');
  const [pricing, setPricing] = useState(null);
  const [savingPricing, setSavingPricing] = useState(false);
  const [priceSyncing, setPriceSyncing] = useState(false);
  const [priceProgress, setPriceProgress] = useState(null);

  const load = async () => {
    try {
      const [s, l] = await Promise.all([
        axios.get(`${API}/danube/status`, auth()),
        axios.get(`${API}/enterprise/locations`, auth()),
      ]);
      setStatus(s.data);
      const d = s.data || {};
      setPricing({
        price_markup_type: d.price_markup_type || 'none',
        price_markup_value: d.price_markup_value || '',
        shipping_fee_enabled: !!d.shipping_fee_enabled,
        shipping_fee_threshold: d.shipping_fee_threshold || '',
        shipping_fee_amount: d.shipping_fee_amount || '',
        sale_duration_years: d.sale_duration_years || 2,
      });
      setLocations(l.data.locations || l.data || []);
      if (s.data?.connected && s.data?.zoho_invoice_enabled) loadZohoCustomers();
    } catch (e) {
      toast.error('Failed to load Danube Home status');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const loadZohoCustomers = async () => {
    try {
      const r = await axios.get(`${API}/danube/zoho-customers`, auth());
      setZohoCustomers(r.data.customers || []);
    } catch (e) { /* zoho may not be connected */ }
  };

  const connect = async () => {
    if (!email.trim() || !password) { toast.error('Enter your Danube Seller Hub email and password'); return; }
    setConnecting(true);
    try {
      const r = await axios.post(`${API}/danube/connect`, { email: email.trim(), password }, auth());
      toast.success(`Connected to Danube Home as ${r.data.business_name || 'seller'}`);
      setEmail(''); setPassword('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Connection failed');
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Danube Home? Stock sync and order polling will stop.')) return;
    try { await axios.post(`${API}/danube/disconnect`, {}, auth()); toast.success('Disconnected'); load(); }
    catch { toast.error('Failed to disconnect'); }
  };

  const saveSetting = async (patch) => {
    try {
      await axios.put(`${API}/danube/settings`, patch, auth());
      setStatus((s) => ({ ...s, ...patch }));
      if (patch.zoho_invoice_enabled) loadZohoCustomers();
      toast.success('Settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };

  const savePricing = async () => {
    setSavingPricing(true);
    await saveSetting({
      price_markup_type: pricing.price_markup_type || 'none',
      price_markup_value: parseFloat(pricing.price_markup_value) || 0,
      shipping_fee_enabled: !!pricing.shipping_fee_enabled,
      shipping_fee_threshold: parseFloat(pricing.shipping_fee_threshold) || 0,
      shipping_fee_amount: parseFloat(pricing.shipping_fee_amount) || 0,
      sale_duration_years: Number(pricing.sale_duration_years) || 2,
    });
    setSavingPricing(false);
  };

  const syncPrices = async () => {
    setPriceSyncing(true);
    setPriceProgress(null);
    try {
      const guard = await checkMarginGuard('danube');
      if (!guard.proceed) { setPriceSyncing(false); return; }
      const start = await axios.post(`${API}/danube/sync-prices`, { ignore_margin_guard: guard.ignore }, auth());
      const jobId = start.data.job_id;
      for (let i = 0; i < 200; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const jr = await axios.get(`${API}/danube/price-job/${jobId}`, auth());
        const job = jr.data;
        if (job.progress) setPriceProgress(job.progress);
        if (job.status === 'running') continue;
        if (job.status === 'failed') {
          toast.error(job.error || 'Price sync failed');
        } else {
          const res = job.result || {};
          toast.success(`Prices pushed — ${res.pushed || 0} product(s)${res.failed_count ? `, ${res.failed_count} failed` : ''}${res.blocked_below_cost ? `, ${res.blocked_below_cost} blocked below cost` : ''}${res.skipped_not_on_danube ? `, ${res.skipped_not_on_danube} not found on Danube` : ''}`);
        }
        setPriceSyncing(false);
        setPriceProgress(null);
        return;
      }
      toast.info('Price sync is still running in the background — check back in a few minutes');
    } catch (e) { toast.error(e.response?.data?.detail || 'Price sync failed'); }
    finally { setPriceSyncing(false); setPriceProgress(null); }
  };

  const addMapping = async () => {
    if (!newMap.app_location_id) { toast.error('Select a warehouse'); return; }
    if (!newMap.countries.length) { toast.error('Select at least one country'); return; }
    try {
      await axios.post(`${API}/danube/mappings`, newMap, auth());
      toast.success('Warehouse mapped');
      setNewMap({ app_location_id: '', countries: ['ae'], stock_buffer: 0 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add mapping'); }
  };

  const deleteMapping = async (id) => {
    try { await axios.delete(`${API}/danube/mappings/${id}`, auth()); toast.success('Mapping removed'); load(); }
    catch { toast.error('Could not remove'); }
  };

  const syncStock = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/danube/sync-stock`, {}, auth());
      const pushed = (r.data.results || []).reduce((a, x) => a + (x.pushed || 0), 0);
      toast.success(`Stock synced — ${pushed} updates pushed to Danube`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Sync failed'); }
    finally { setSyncing(false); }
  };

  const toggleCountry = (code) => setNewMap((m) => ({
    ...m, countries: m.countries.includes(code) ? m.countries.filter((c) => c !== code) : [...m.countries, code],
  }));

  const syncOrders = async () => {
    setSyncingOrders(true);
    try {
      const r = await axios.post(`${API}/danube/sync-orders`, ordersFromDate ? { from_date: ordersFromDate } : {}, auth());
      toast.success(r.data.message || 'Danube orders synced');
    } catch (e) { toast.error(e.response?.data?.detail || 'Order sync failed'); }
    finally { setSyncingOrders(false); }
  };

  if (loading) return <div className="p-8 text-wt-text-muted" data-testid="danube-loading">Loading…</div>;
  const connected = status?.connected;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="danube-page">
      <button onClick={() => navigate('/integrations')} className="flex items-center gap-2 text-sm text-wt-text-muted hover:text-wt-text-primary" data-testid="danube-back-btn">
        <ArrowLeft className="h-4 w-4" /> Back to Integrations
      </button>

      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center">
          <Store className="h-6 w-6 text-black" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-wt-text-primary">Danube Home</h1>
          <p className="text-sm text-wt-text-muted">Sync inventory to GCC countries, auto-ingest & fulfil orders, invoice via Zoho Books.</p>
        </div>
        {connected && <Badge className="ml-auto bg-green-500/15 text-green-500 border border-green-500/30" data-testid="danube-connected-badge">Connected</Badge>}
      </div>

      {/* Connection */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="rounded-xl border border-wt-border bg-wt-surface p-5 space-y-4">
        <div className="flex items-center gap-2"><Plug className="h-4 w-4 text-wt-gold" /><h2 className="font-semibold text-wt-text-primary">Connection</h2></div>
        {!connected ? (
          <div className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Seller Hub Email</Label><Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" data-testid="danube-email-input" /></div>
              <div><Label>Password</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="danube-password-input" /></div>
            </div>
            <Button onClick={connect} disabled={connecting} className="bg-wt-gold hover:bg-wt-gold-hover text-black" data-testid="danube-connect-btn">
              {connecting ? 'Connecting…' : 'Connect Danube Home'}
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="text-sm text-wt-text-secondary">
              <p><b className="text-wt-text-primary">{status.business_name}</b></p>
              <p className="text-wt-text-muted">{status.email} · Seller {status.seller_id} · Web ID {status.supplier_web_id}</p>
            </div>
            <Button variant="outline" onClick={disconnect} className="border-red-500/30 text-red-400" data-testid="danube-disconnect-btn">Disconnect</Button>
          </div>
        )}
      </motion.div>

      {connected && (
        <>
          {/* Automation settings */}
          <div className="rounded-xl border border-wt-border bg-wt-surface p-5 space-y-4">
            <div className="flex items-center gap-2"><Zap className="h-4 w-4 text-wt-gold" /><h2 className="font-semibold text-wt-text-primary">Order Automation</h2></div>
            <div className="flex items-center justify-between">
              <div><p className="text-sm text-wt-text-primary">Auto-accept new orders</p><p className="text-xs text-wt-text-muted">Automatically accept incoming orders with the latest allowed delivery date (max time to fulfil). Turn off to accept each order yourself.</p></div>
              <Switch checked={!!status.auto_accept} onCheckedChange={(v) => saveSetting({ auto_accept: v })} data-testid="danube-auto-accept-switch" />
            </div>
            <div className="border-t border-wt-border pt-4 flex items-center justify-between">
              <div><p className="text-sm text-wt-text-primary">Sync orders to Shopify</p><p className="text-xs text-wt-text-muted">Also create each Danube order on Shopify automatically. The Danube order number stays the main number everywhere; the Shopify number is shown alongside.</p></div>
              <Switch checked={!!status.sync_to_shopify} onCheckedChange={(v) => saveSetting({ sync_to_shopify: v })} data-testid="danube-shopify-sync-switch" />
            </div>
            {!!status.sync_to_shopify && (
              <div className="flex items-center justify-between border-t border-wt-border pt-4 mt-4">
                <div><p className="text-sm text-wt-text-primary">Shopify push delay (minutes)</p><p className="text-xs text-wt-text-muted">Orders appear in the app instantly but reach Shopify only after this delay, so Danube has time to send the full product info (merchant SKU). Recommended: 5.</p></div>
                <Input type="number" min="0" max="120" defaultValue={status.shopify_push_delay_min ?? 5}
                  onBlur={(e) => saveSetting({ shopify_push_delay_min: Math.max(0, Number(e.target.value) || 0) })}
                  className="w-24 h-9 text-right" data-testid="danube-shopify-delay-input" />
              </div>
            )}
            <div className="border-t border-wt-border pt-4 flex items-center justify-between">
              <div><p className="text-sm text-wt-text-primary">Shipping method sent to Danube</p><p className="text-xs text-wt-text-muted">Used when the app marks an order Packed &amp; Shipped on Danube. Self Ship = your own drivers deliver; Easy Ship = Danube's courier (Emirates Post).</p></div>
              <select
                value={status.shipping_method ?? 2}
                onChange={(e) => saveSetting({ shipping_method: Number(e.target.value) })}
                className="h-9 rounded-md border border-wt-border bg-wt-bg text-wt-text-primary text-sm px-2"
                data-testid="danube-shipping-method-select"
              >
                <option value={2}>Self Ship</option>
                <option value={1}>Easy Ship</option>
              </select>
            </div>
            <div className="border-t border-wt-border pt-4 flex items-center justify-between">
              <div><p className="text-sm text-wt-text-primary">Auto-create Zoho Books invoice on delivery</p><p className="text-xs text-wt-text-muted">When an order is delivered, create the Zoho invoice (net of Danube commission) and submit the invoice number to Danube.</p></div>
              <Switch checked={!!status.zoho_invoice_enabled} onCheckedChange={(v) => saveSetting({ zoho_invoice_enabled: v })} data-testid="danube-zoho-switch" />
            </div>
            {status.zoho_invoice_enabled && (
              <div className="border-t border-wt-border pt-4">
                <Label>Bill invoices to (Zoho customer = Danube Home)</Label>
                <Popover open={zohoCustOpen} onOpenChange={setZohoCustOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" className="w-full justify-between border-wt-border bg-wt-bg text-wt-text-primary font-normal" data-testid="danube-zoho-customer-select">
                      {(zohoCustomers.find((c) => c.id === status.zoho_customer_id) || {}).name || status.zoho_customer_name || 'Select the Danube billing customer'}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                    <Command>
                      <CommandInput placeholder={`Search ${zohoCustomers.length} customers...`} data-testid="danube-zoho-customer-search" />
                      <CommandList className="max-h-64">
                        <CommandEmpty>No customer found.</CommandEmpty>
                        {zohoCustomers.map((c) => (
                          <CommandItem key={c.id} value={c.name || c.id}
                            data-testid={`zoho-cust-${c.id}`}
                            onSelect={() => { saveSetting({ zoho_customer_id: c.id, zoho_customer_name: c.name }); setZohoCustOpen(false); }}>
                            <Check className={`mr-2 h-4 w-4 ${status.zoho_customer_id === c.id ? 'opacity-100' : 'opacity-0'}`} />
                            {c.name}
                          </CommandItem>
                        ))}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-wt-text-muted mt-1">All Danube invoices are billed to Danube at the post-commission amount, not to the end customer.</p>
              </div>
            )}
          </div>

          {/* Price sync */}
          {pricing && (
            <div className="rounded-xl border border-wt-border bg-wt-surface p-5 space-y-4" data-testid="danube-pricing-card">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2"><Tag className="h-4 w-4 text-wt-gold" /><h2 className="font-semibold text-wt-text-primary">Price Sync — MSP &amp; Sale Price</h2></div>
                <Button size="sm" onClick={syncPrices} disabled={priceSyncing} className="bg-wt-gold hover:bg-wt-gold-hover text-black" data-testid="danube-sync-prices-btn">
                  <RefreshCw className={`h-4 w-4 mr-1 ${priceSyncing ? 'animate-spin' : ''}`} /> Sync Prices to Danube Now
                </Button>
              </div>
              <p className="text-xs text-wt-text-muted">Your <b>compare-at price</b> goes to Danube's <b>MSP</b> and your selling price becomes the <b>Sale Price</b>. Products without a compare-at price get MSP = Sale Price. UAE (AED) only.</p>
              {priceSyncing && (
                <div className="rounded-lg border border-wt-gold/30 bg-wt-gold/5 p-3 space-y-2" data-testid="danube-price-progress">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-wt-text-primary">
                      {priceProgress ? `Pushing ${priceProgress.done}/${priceProgress.total} products — ${priceProgress.pushed} ok${priceProgress.failed ? `, ${priceProgress.failed} failed` : ''}` : 'Matching your SKUs with the Danube catalog…'}
                    </span>
                    <span className="text-wt-gold font-semibold" data-testid="danube-price-progress-pct">
                      {priceProgress && priceProgress.total ? `${Math.round((priceProgress.done / priceProgress.total) * 100)}%` : ''}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-wt-bg overflow-hidden">
                    <div className="h-full rounded-full bg-wt-gold transition-all duration-500"
                      style={{ width: priceProgress && priceProgress.total ? `${Math.max(4, Math.round((priceProgress.done / priceProgress.total) * 100))}%` : '4%' }} />
                  </div>
                </div>
              )}
              <ChannelPricingRules pricing={pricing} setPricing={setPricing} idPrefix="danube" priceLabel="Sale Price" compareLabel="MSP" />
              <div className="border-t border-wt-border pt-4 flex items-center justify-between gap-3">
                <div><p className="text-sm text-wt-text-primary">Sale price duration</p><p className="text-xs text-wt-text-muted">Danube requires dates on the Sale Price — each sync sets it from today for this long.</p></div>
                <Select value={String(pricing.sale_duration_years || 2)} onValueChange={(v) => setPricing((p) => ({ ...p, sale_duration_years: Number(v) }))}>
                  <SelectTrigger className="w-28" data-testid="danube-sale-duration-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 5, 10].map((y) => <SelectItem key={y} value={String(y)} data-testid={`danube-duration-${y}`}>{y} year{y > 1 ? 's' : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button size="sm" onClick={savePricing} disabled={savingPricing} className="bg-wt-gold hover:bg-wt-gold-hover text-black" data-testid="danube-save-pricing-btn">
                {savingPricing ? 'Saving…' : 'Save Pricing Rules'}
              </Button>
            </div>
          )}

          {/* Warehouse → country mappings */}
          <div className="rounded-xl border border-wt-border bg-wt-surface p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Warehouse className="h-4 w-4 text-wt-gold" /><h2 className="font-semibold text-wt-text-primary">Inventory Sync — Warehouse → Countries</h2></div>
              <Button size="sm" onClick={syncStock} disabled={syncing} className="bg-wt-gold hover:bg-wt-gold-hover text-black" data-testid="danube-sync-stock-btn">
                <RefreshCw className={`h-4 w-4 mr-1 ${syncing ? 'animate-spin' : ''}`} /> Sync All Stock Now
              </Button>
            </div>

            <div className="rounded-lg border border-wt-border p-3 space-y-3 bg-wt-bg">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Opswarex Warehouse</Label>
                  <Select value={newMap.app_location_id} onValueChange={(v) => setNewMap((m) => ({ ...m, app_location_id: v }))}>
                    <SelectTrigger data-testid="danube-map-location-select"><SelectValue placeholder="Select warehouse" /></SelectTrigger>
                    <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id} data-testid={`danube-loc-${l.id}`}>{l.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Stock Buffer (keep in reserve)</Label>
                  <Input type="number" min="0" value={newMap.stock_buffer} onChange={(e) => setNewMap((m) => ({ ...m, stock_buffer: Number(e.target.value) }))} data-testid="danube-map-buffer-input" />
                </div>
              </div>
              <div>
                <Label>Push stock to these countries</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {GCC.map((c) => (
                    <button key={c.code} onClick={() => toggleCountry(c.code)} data-testid={`danube-country-${c.code}`}
                      className={`px-3 py-1 rounded-full text-xs border transition-colors ${newMap.countries.includes(c.code) ? 'bg-wt-gold text-black border-wt-gold' : 'border-wt-border text-wt-text-muted hover:border-wt-gold'}`}>
                      {c.name}
                    </button>
                  ))}
                </div>
              </div>
              <Button size="sm" onClick={addMapping} className="bg-wt-gold hover:bg-wt-gold-hover text-black" data-testid="danube-add-mapping-btn">Add Mapping</Button>
            </div>

            <div className="space-y-2" data-testid="danube-mappings-list">
              {(status.mappings || []).length === 0 && <p className="text-sm text-wt-text-muted">No warehouse mappings yet. Add one above to start syncing stock.</p>}
              {(status.mappings || []).map((m) => (
                <div key={m.id} className="flex items-center justify-between rounded-lg border border-wt-border p-3" data-testid={`danube-mapping-${m.id}`}>
                  <div className="text-sm">
                    <p className="text-wt-text-primary font-medium">{m.app_location_name}</p>
                    <p className="text-wt-text-muted text-xs">→ {(m.countries || []).map((c) => c.toUpperCase()).join(', ')} {m.stock_buffer ? `· buffer ${m.stock_buffer}` : ''}</p>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => deleteMapping(m.id)} data-testid={`danube-del-mapping-${m.id}`}><Trash2 className="h-4 w-4 text-red-400" /></Button>
                </div>
              ))}
            </div>
            {status.last_sync && <p className="text-xs text-wt-text-muted">Last sync: {new Date(status.last_sync.created_at).toLocaleString()} ({status.last_sync.trigger})</p>}
          </div>

          <div className="rounded-xl border border-wt-border bg-wt-surface p-5">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-wt-gold" /><h2 className="font-semibold text-wt-text-primary">Order Fulfilment</h2></div>
              <div className="flex items-center gap-2">
                <input type="date" value={ordersFromDate} onChange={(e) => setOrdersFromDate(e.target.value)}
                  title="Only sync orders placed on or after this date"
                  className="h-9 px-2 rounded-md bg-wt-bg border border-wt-border text-wt-text-primary text-xs"
                  data-testid="danube-orders-from-date" />
                <Button size="sm" variant="outline" onClick={syncOrders} disabled={syncingOrders} className="border-wt-border text-wt-text-primary" data-testid="danube-sync-orders-btn">
                  <RefreshCw className={`h-4 w-4 mr-1 ${syncingOrders ? 'animate-spin' : ''}`} /> {syncingOrders ? 'Syncing…' : 'Sync Orders Now'}
                </Button>
              </div>
            </div>
            <p className="text-sm text-wt-text-muted">New Danube orders appear in <button onClick={() => navigate('/orders')} className="text-wt-gold underline" data-testid="danube-goto-orders">Orders</button> (auto-polled every 5 min, or sync now above). Open an order to Accept, mark Ready to Pickup, and Set Delivered. Bulky/express orders route through your driver workflow; on delivery, the delivery date & POD are pushed back to Danube{status.zoho_invoice_enabled ? ' and the Zoho invoice is created automatically' : ''}.</p>
          </div>
        </>
      )}
    </div>
  );
}
